import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TextScramble from '../components/TextScramble';
import { ArrowLeft, Eye, EyeOff, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function extractError(err, fallback = 'Une erreur est survenue') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length > 0) return d[0]?.msg || fallback;
  return fallback;
}

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  // step: 1 = email, 2 = code + new password, 3 = success
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const codeRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (step === 2 && codeRefs[0].current) codeRefs[0].current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/customer/password/request`, { email: email.trim().toLowerCase() });
      setInfo(res.data?.message || 'Code envoyé');
      setStep(2);
    } catch (err) {
      setError(extractError(err, "Impossible d'envoyer le code"));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (i, value) => {
    const v = value.replace(/\D/g, '').slice(0, 1);
    const next = [...code];
    next[i] = v;
    setCode(next);
    if (v && i < 3) codeRefs[i + 1].current?.focus();
  };

  const handleCodeKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) {
      codeRefs[i - 1].current?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 4);
    if (pasted.length > 0) {
      e.preventDefault();
      const next = ['', '', '', ''];
      for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
      setCode(next);
      const focusIdx = Math.min(pasted.length, 3);
      codeRefs[focusIdx].current?.focus();
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    const codeStr = code.join('');
    if (codeStr.length !== 4) {
      setError('Veuillez saisir les 4 chiffres du code');
      return;
    }
    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/customer/password/reset`, {
        email: email.trim().toLowerCase(),
        code: codeStr,
        new_password: newPassword,
      });
      setStep(3);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(extractError(err, 'Code invalide ou expiré'));
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/customer/password/request`, { email: email.trim().toLowerCase() });
      setInfo(res.data?.message || 'Un nouveau code vous a été envoyé');
      setCode(['', '', '', '']);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen pt-24 sm:pt-28 pb-24 flex items-center justify-center">
      <div className="w-full max-w-md px-4">
        <Link
          to="/login"
          data-testid="back-to-login"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors mb-10"
        >
          <ArrowLeft size={14} /> Retour à la connexion
        </Link>

        <div className="text-center mb-12">
          <TextScramble
            text={step === 3 ? 'Confirmé' : 'Mot de passe oublié'}
            className="text-4xl sm:text-5xl tracking-tighter font-light text-black"
            delay={0.2}
          />
          {step === 1 && (
            <p className="mt-3 text-sm text-black/50 leading-relaxed">
              Entrez votre email. Nous vous enverrons un code à 4 chiffres pour réinitialiser votre mot de passe.
            </p>
          )}
          {step === 2 && (
            <p className="mt-3 text-sm text-black/50 leading-relaxed">
              Un code à 4 chiffres a été envoyé à <span className="font-semibold text-black/80">{email}</span>.<br/>
              Vérifiez votre boîte de réception (et les spams).
            </p>
          )}
          {step === 3 && (
            <p className="mt-3 text-sm text-black/50 leading-relaxed">
              Votre mot de passe a été mis à jour. Redirection en cours…
            </p>
          )}
        </div>

        {error && (
          <div data-testid="forgot-error" className="mb-6 p-3 text-sm text-center" style={{ backgroundColor: '#B800000D', color: '#B80000' }}>
            {error}
          </div>
        )}
        {info && !error && step !== 3 && (
          <div data-testid="forgot-info" className="mb-6 p-3 text-sm text-center bg-[#EDE8DE] text-black/60">
            {info}
          </div>
        )}

        {step === 1 && (
          <form data-testid="request-form" onSubmit={handleRequest} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Email</label>
              <input
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="votre@email.com"
                className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors"
              />
            </div>
            <button
              data-testid="request-code-btn"
              type="submit"
              disabled={loading}
              className="btn-primary w-full mt-8 disabled:opacity-50"
            >
              {loading ? 'Envoi du code…' : 'Envoyer le code'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form data-testid="reset-form" onSubmit={handleReset} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-3 text-center">
                Code à 4 chiffres
              </label>
              <div className="flex justify-center gap-3">
                {code.map((digit, i) => (
                  <input
                    key={i}
                    ref={codeRefs[i]}
                    data-testid={`code-input-${i}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleCodeChange(i, e.target.value)}
                    onKeyDown={e => handleCodeKeyDown(i, e)}
                    onPaste={handleCodePaste}
                    className="w-14 h-16 text-center text-2xl font-semibold border border-black/15 bg-[#FFFBF4] focus:border-[#B80000] focus:outline-none focus:ring-2 focus:ring-[#B80000]/10 transition-all"
                    style={{ caretColor: '#B80000' }}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 block mb-2">Nouveau mot de passe</label>
              <div className="relative">
                <input
                  data-testid="new-password-input"
                  type={showPwd ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Au moins 6 caractères"
                  className="bg-transparent border-b border-black/20 focus:border-black outline-none py-3 w-full text-sm transition-colors pr-10"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-0 top-3 text-black/30 hover:text-black/60">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading || code.join('').length !== 4}
              className="btn-primary w-full mt-4 disabled:opacity-50"
            >
              {loading ? 'Validation…' : 'Réinitialiser le mot de passe'}
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={resendCode}
                disabled={loading}
                data-testid="resend-code-btn"
                className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors disabled:opacity-40"
              >
                Renvoyer un code
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div data-testid="reset-success" className="flex flex-col items-center py-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#B80000' }}>
              <Check size={32} className="text-white" strokeWidth={2} />
            </div>
            <Link to="/login" className="btn-primary">Retour à la connexion</Link>
          </div>
        )}
      </div>
    </div>
  );
}
